/**
 * Copy something to the clipboard from an icon button. The icon turns into a check for a
 * moment and the label says "Copied"; a blocked clipboard says so in the tooltip instead of
 * failing silently (never a toast: feedback/no-error-toast).
 */
import { Check, Copy } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { IconButton } from './IconButton.tsx';
import type { IconButtonProps } from './IconButton.tsx';

export interface CopyButtonProps extends Omit<IconButtonProps, 'icon' | 'onClick' | 'label'> {
  /** The text to copy, or a function that makes it when pressed. */
  text: string | (() => string);
  /** What the button copies, as a verb phrase: "Copy code", "Copy the answer". */
  label?: string;
}

/** Copy text; resolves false when the browser refused. */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Fall through to the selection copy below.
  }
  try {
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand('copy');
    area.remove();
    return ok;
  } catch {
    return false;
  }
}

export function CopyButton({ text, label = 'Copy', size = 'xs', ...rest }: CopyButtonProps) {
  const [state, setState] = useState<'idle' | 'copied' | 'blocked'>('idle');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => void (timer.current && clearTimeout(timer.current)), []);
  const press = async () => {
    const ok = await copyText(typeof text === 'function' ? text() : text);
    setState(ok ? 'copied' : 'blocked');
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setState('idle'), 1600);
  };
  const shown = state === 'copied' ? 'Copied' : state === 'blocked' ? 'The browser blocked the clipboard. Select the text and copy it instead' : label;
  return <IconButton {...rest} size={size} label={shown} icon={state === 'copied' ? <Check /> : <Copy />} onClick={() => void press()} />;
}
